/**
 * Field — wrapper d'un contrôle de formulaire.
 *
 * Compose `ui-field`, `ui-label`, `ui-help`, `ui-error` du design system,
 * et propage `htmlFor` + `aria-describedby` + `aria-invalid` automatiquement.
 *
 *   <Field label="Email" required error={errors.email?.message}>
 *     {(controlProps) => <Input type="email" {...register('email')} {...controlProps} />}
 *   </Field>
 */

import { useId, type ReactElement, type ReactNode } from 'react'

export interface FieldControlProps {
  id: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  required?: boolean
}

interface FieldProps {
  label: string
  required?: boolean
  /** Texte d'aide sous le champ. Accepte du `ReactNode` pour permettre du
   *  rich content (calculs dynamiques, lignes multiples) — la string simple
   *  reste le cas dominant. */
  helper?: ReactNode
  error?: string
  hideLabel?: boolean
  /**
   * Slot aligné à droite sur la ligne du label. Pour héberger les méta du
   * champ (compteur de caractères, hint orthographe, bouton "Aide-moi") en
   * libérant l'espace vertical sous la textarea. Ignoré si `hideLabel`.
   */
  labelActions?: ReactNode
  children: (controlProps: FieldControlProps) => ReactElement | ReactNode
}

export default function Field({
  label,
  required = false,
  helper,
  error,
  hideLabel = false,
  labelActions,
  children,
}: FieldProps) {
  const baseId = useId()
  const controlId = `${baseId}-control`
  const helperId = helper ? `${baseId}-helper` : undefined
  const errorId = error ? `${baseId}-error` : undefined
  const describedBy = [helperId, errorId].filter(Boolean).join(' ') || undefined

  const labelEl = (
    <label htmlFor={controlId} className={hideLabel ? 'sr-only' : 'ui-label'}>
      {label}
      {required && (
        <span aria-hidden="true" className="ui-text-danger">
          {' *'}
        </span>
      )}
    </label>
  )

  return (
    <div className="ui-field">
      {labelActions && !hideLabel ? (
        <div className="ui-field-header">
          {labelEl}
          <div className="ui-field-actions">{labelActions}</div>
        </div>
      ) : (
        labelEl
      )}
      {children({
        id: controlId,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        required,
      })}
      {helper && !error && (
        <span id={helperId} className="ui-help">
          {helper}
        </span>
      )}
      {error && (
        <span id={errorId} className="ui-error" role="alert">
          {error}
        </span>
      )}
    </div>
  )
}
